import { makeStyles } from '@mui/styles';
import Popover from 'apollo-react/components/Popover';
import TextField from 'apollo-react/components/TextField';
import TreeItem from 'apollo-react/components/TreeItem';
import TreeView from 'apollo-react/components/TreeView';
import Typography from 'apollo-react/components/Typography';
import { ArrowDown, Search } from 'apollo-react-icons';
import Close from 'apollo-react-icons/Close';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { getHighlightedText } from './Sidenav/customMenus/utils';
import { DEBOUNCE_SEARCH_TIME } from '../constants/Common';
import { INPUT_ICON_DEFAULT_SIZE } from '../constants/CommonStylesConstants';
import { useTranslation } from '../custom-hooks/useTranslation';
import { _find } from '../form-designer/utils/form-manager.utils';
import { getUITA } from '../utils/commonUtils';
import { filterTreeData, getExpandedNodeIds } from '../utils/repositoryConfigurationUtils';

const MIN_SELECT_WIDTH = 250;

const useStyles = makeStyles({
  dropdownInput: {
    cursor: 'pointer',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  searchTextInput: {
    flex: 1,
    margin: '10px 10px 5px 10px',
  },
  emptyMessage: {
    textAlign: 'center',
    margin: '25px 0px',
  },
  icon: INPUT_ICON_DEFAULT_SIZE,
  rotateIcon: {
    transform: 'rotate(180deg)',
  },
  menuItemsContainer: {
    maxHeight: 350,
    overflowY: 'auto',
    paddingBottom: 5,
  },
  dropdown: {
    margin: 3,
    paddingRight: 3,
    paddingLeft: 0,
    minWidth: MIN_SELECT_WIDTH,
    '& button': {
      '&:hover': {
        backgroundColor: 'transparent !important',
        color: '#4d4d4d !important',
      },
    },
  },
  highlightedText: {
    fontWeight: 'bold',
  },
});

const renderTree = ({ id, value, label, childrenNodes }, searchText, classes) => {
  const fieldName = value ? ` (${value.split('.')?.[1] || value.split('.')[0]})` : '';
  const labelToDisplay = `${label}${fieldName}`;

  return (
    <TreeItem
      key={id}
      nodeId={id}
      label={
        searchText
          ? getHighlightedText(labelToDisplay, searchText, classes.highlightedText)
          : labelToDisplay
      }
      ui-ta={getUITA('DataModelTreeItem', labelToDisplay)}
    >
      {Array.isArray(childrenNodes)
        ? childrenNodes.map((node) => renderTree(node, searchText, classes))
        : null}
    </TreeItem>
  );
};

const SearchInput = ({ handleSearch, clearSearch }) => {
  const classes = useStyles();
  const translateString = useTranslation();

  const searchInputRef = useRef();

  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    searchInputRef.current.focus();

    return () => {
      setInputValue('');
      clearSearch();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = ({ target: { value } }) => {
    const newSearchText = value.length === 1 ? value.trim() : value;

    setInputValue(newSearchText);
    handleSearch(newSearchText);
  };

  const clearSearchText = () => {
    setInputValue('');
    searchInputRef.current.focus();

    clearSearch();
  };

  return (
    <div style={{ display: 'flex' }}>
      <TextField
        className={classes.searchTextInput}
        startIcon={<Search className={classes.icon} />}
        placeholder={translateString('Search')}
        icon={inputValue !== '' ? <Close className={classes.icon} /> : null}
        iconProps={{ onClick: clearSearchText }}
        value={inputValue ?? ''}
        inputRef={searchInputRef}
        onChange={handleChange}
        ui-ta={getUITA('SearchNameSelect', 'SearchInput')}
      />
    </div>
  );
};

const TreeSearchDropdown = ({ row, columns, value, onChange, disabled, showLabel, ...props }) => {
  const classes = useStyles();
  const translateString = useTranslation();

  const anchorElRef = useRef(null);

  const [isListOpen, setIsListOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [expanded, setExpanded] = useState([]);
  const [click, setClick] = useState(0);

  const defaultEl = columns[0].id;

  const treeData = useMemo(() => (searchText ? filterTreeData(columns, searchText) : columns), [
    columns,
    searchText,
  ]);

  useEffect(() => {
    const expandedNodeIds = searchText ? getExpandedNodeIds(treeData) : [defaultEl];
    setExpanded(expandedNodeIds);
  }, [treeData, defaultEl, searchText]);

  const valueToDisplay = useMemo(() => {
    if (value) {
      const node = _find(treeData, 'value', value) || {};
      const fieldName = node.value
        ? ` (${node.value.split('.')?.[1] || node.value.split('.')[0]})`
        : '';

      return `${node.label}${fieldName}`;
    }

    return '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleOnMouseDown = () => {
    row.handleSelectRow && row.handleSelectRow({ rowId: row.rowId });
  };

  const debouncedHandleSearch = useMemo(
    () => _.debounce((value) => setSearchText(value), DEBOUNCE_SEARCH_TIME),
    []
  );

  const handleExpandedNodes = (_, nodeIds) => setExpanded(nodeIds);

  const handleOpen = () => {
    if (isListOpen) {
      setIsListOpen(false);
      setSearchText('');
      setExpanded([defaultEl]);
    } else {
      setIsListOpen(true);
    }
  };

  const handleSelect = (e, nodeId) => {
    e.stopPropagation();

    setClick((prev) => prev + 1);

    setTimeout(() => {
      setClick(0);
    }, 200);

    const nodeValue = _find(treeData, 'id', nodeId[0]);

    if (nodeValue?.isSelectable && click > 0) {
      onChange(nodeValue.value);
      handleOpen();
    }
  };

  const dropdownMinWidth =
    anchorElRef?.current?.offsetWidth > MIN_SELECT_WIDTH
      ? anchorElRef?.current?.offsetWidth
      : MIN_SELECT_WIDTH;

  return (
    <>
      <TextField
        {...props}
        className={classes.dropdown}
        icon={<ArrowDown className={isListOpen ? classes.rotateIcon : null} />}
        onClick={handleOpen}
        ref={anchorElRef}
        value={valueToDisplay}
        fullWidth
        size="small"
        disabled={disabled}
        onMouseDown={handleOnMouseDown}
        label={showLabel ? translateString('Column') : null}
        inputProps={{
          readOnly: true,
          className: classes.dropdownInput,
        }}
        ui-ta={getUITA('SearchNameSelect')}
      />
      <Popover
        onClose={handleOpen}
        open={isListOpen}
        anchorEl={anchorElRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            style: { minWidth: dropdownMinWidth, minHeight: 150 },
          },
        }}
      >
        {isListOpen && (
          <>
            {columns.length && (
              <SearchInput
                handleSearch={debouncedHandleSearch}
                clearSearch={() => setSearchText('')}
              />
            )}
            <div className={classes.menuItemsContainer}>
              {columns.length && treeData.length ? (
                <TreeView
                  variant="folder"
                  defaultExpanded={[defaultEl]}
                  onNodeSelect={handleSelect}
                  onNodeToggle={handleExpandedNodes}
                  expanded={expanded}
                  multiSelect
                >
                  {treeData.map((node) => renderTree(node, searchText, classes))}
                </TreeView>
              ) : (
                <Typography className={classes.emptyMessage}>
                  {translateString('No data to display')}
                </Typography>
              )}
            </div>
          </>
        )}
      </Popover>
    </>
  );
};

export default TreeSearchDropdown;
